import axios from "axios";
import { store } from "../redux/store";
import { logout, setAccessToken } from "../redux/authSlice";

// =====================================================
// AXIOS INSTANCE (MUST HAVE withCredentials)
// =====================================================

export const axiosInstance = axios.create({
    baseURL: "https://hrmsstagingapi.hashstack.in/api",
    //  baseURL:'https://hrms-prodapi.hashstack.in/api',
    withCredentials: true, // 🔥 Mandatory for cookies
});

// Helper to get token from redux
const getAuthToken = () => store.getState().auth.accessToken;

// =====================================================
// GLOBAL REFRESH HANDLING STATE
// =====================================================
let isRefreshing = false;
let failedQueue = [];

// Add after line: let failedQueue = [];

// Function to call logout API
const callLogoutAPI = async () => {
    try {
        const token = getAuthToken();
        if (token) {
            await axiosInstance.post('/organization/logout', {}, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            console.log("Logout API called successfully");
        }
    } catch (error) {
        console.error("Logout API call failed:", error);
        // Continue with logout even if API fails
    }
};

const processQueue = (error, token = null) => {
    failedQueue.forEach(p => {
        if (error) p.reject(error);
        else p.resolve(token);
    });
    failedQueue = [];
};

// =====================================================
// REQUEST INTERCEPTOR
// =====================================================
axiosInstance.interceptors.request.use(
    config => {
        const token = getAuthToken();

        // Skip Authorization header ONLY for refresh-token request
        if (!config.url.includes("/auth/refresh-token")) {
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }

        return config;
    },
    error => Promise.reject(error)
);

// =====================================================
// RESPONSE INTERCEPTOR (TOKEN REFRESH FLOW)
// =====================================================
axiosInstance.interceptors.response.use(
    res => res,
    async error => {
        const originalRequest = error.config;
        const status = error.response?.status;

        if ((status === 401 || status === 403) && !originalRequest._retry) {
            originalRequest._retry = true;

            // If refresh already running → queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return axiosInstance(originalRequest);
                    })
                    .catch(err => Promise.reject(err));
            }

            isRefreshing = true;

            try {
                // IMPORTANT: MUST USE axiosInstance (not axios)
                const refreshResponse = await axiosInstance.post(
                    "/auth/refresh-token"
                );

                const newToken =
                    refreshResponse.data.message.accessToken;

                // Store the new access token
                store.dispatch(setAccessToken({ accessToken: newToken }));

                processQueue(null, newToken);
                isRefreshing = false;

                // Retry the original request with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axiosInstance(originalRequest);

            } catch (err) {
                processQueue(err, null);
                isRefreshing = false;

                if (err.response?.status === 403 || err.response?.status === 401) {
        // Call logout API before dispatching logout
        await callLogoutAPI();
    }

                // Refresh failed → logout user
                store.dispatch(logout());
                return Promise.reject(err);
            }
        }

        return Promise.reject(error);
    }
);

// ==========================================================
// B. ORIGINAL HELPER FUNCTIONS (PRESERVED)
// ==========================================================

// Function for POST API without auth token (used for login/public routes)
export const localAxios = (endpoint, data, contentType = "application/json") => ({
    method: "POST",
    url: `${axiosInstance.defaults.baseURL}${endpoint}`,
    headers: {
        "Content-Type": contentType,
    },
    data,
     withCredentials: true,
});

// Function for POST requests with auth token
export const localAxiosToken = (endpoint, data, contentType = "application/json") => {
    const token = getAuthToken();
    return {
        method: "POST",
        url: `${axiosInstance.defaults.baseURL}${endpoint}`,
        headers: {
            "Content-Type": contentType,
            // Manual header addition is preserved for compatibility
            ...(token && { Authorization: `Bearer ${token}` }),
        },
        data,
         withCredentials: true,
    };
};

// Function for PUT requests
export const localAxiosPut = (endpoint, data, contentType = "application/json") => {
    const token = getAuthToken();
    return {
        method: "PUT",
        url: `${axiosInstance.defaults.baseURL}${endpoint}`,
        headers: {
            "Content-Type": contentType,
            // Manual header addition is preserved for compatibility
            ...(token && { Authorization: `Bearer ${token}` }),
        },
        data,
         withCredentials: true,
    };
};

// Function for GET API requests
export const localAxiosGet = (endpoint, contentType = "application/json") => {
    const token = getAuthToken();
    const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    return {
        method: "GET",
        url: `${axiosInstance.defaults.baseURL}${formattedEndpoint}`,
        headers: {
            "Content-Type": contentType,
            // Manual header addition is preserved for compatibility
            ...(token && { Authorization: `Bearer ${token}` }),
        },
         withCredentials: true,
    };
};

// Function for DELETE API requests
export const localAxiosDelete = (endpoint, contentType = "application/json") => {
    const token = getAuthToken();
    const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    return {
        method: "DELETE",
        url: `${axiosInstance.defaults.baseURL}${formattedEndpoint}`,
        headers: {
            "Content-Type": contentType,
            // Manual header addition is preserved for compatibility
            ...(token && { Authorization: `Bearer ${token}` }),
        },
         withCredentials: true,
    };
};

// Function for file upload requests (multipart/form-data)
export const localAxiosFileUpload = (endpoint, data) => {
    const token = getAuthToken();
    return {
        method: "POST",
        url: `${axiosInstance.defaults.baseURL}${endpoint}`,
        headers: {
            "Content-Type": "multipart/form-data",
            // Manual header addition is preserved for compatibility
            ...(token && { Authorization: `Bearer ${token}` }),
        },
        data,
         withCredentials: true,
    };
};

